import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes, Keyframes } from 'styled-components';
import { useRouter } from 'next/compat/router';
import usePaths from 'hooks/use-paths';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';

const HIGHLIGHT_GOLD = '#f2c14d';
const LENS_GOLD = '#e2bb5d';
const LENS_GREEN = '#003d29';
const GREEN_TRANSPARENT = 'rgba(0, 61, 41, 0)';

const SVG_PAD = 12;

const travelFrom = (adjust: number): Keyframes => keyframes`
  from { stroke-dashoffset: ${adjust}; }
  to { stroke-dashoffset: ${adjust - 100}; }
`;

const baseTravel = travelFrom(0);

const colorPulse = keyframes`
  0%, 25% { stroke: ${LENS_GOLD}; }
  50%, 75% { stroke: ${LENS_GREEN}; }
  100%  { stroke: ${LENS_GOLD}; }
`;

const LENS_LENGTH = 22;
const LENS_TAPER_EXPONENT = 0.75;

type TaperedLayer = { width: number; length: number; alpha: number; travel: Keyframes };

const buildTaperedLayers = (count: number, maxWidth: number, minWidth: number): TaperedLayer[] => {
  const layers: TaperedLayer[] = [];
  let cumulative = 0;
  for (let i = 0; i < count; i += 1) {
    const length = (LENS_LENGTH * (count - i)) / count;
    const width = Math.max(minWidth, maxWidth * (1 - length / LENS_LENGTH) ** LENS_TAPER_EXPONENT);
    const target = (i + 0.7) / count;
    const alpha = (target - cumulative) / (1 - cumulative);
    cumulative = target;
    layers.push({ width, length, alpha, travel: travelFrom((length - LENS_LENGTH) / 2) });
  }
  return layers;
};

const lensLayers = buildTaperedLayers(12, 1.5, 0.1);
const glowLayers = buildTaperedLayers(6, 6, 1.2);

function parseRgb(color: string): [number, number, number] | null {
  const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(color);
  return m ? [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)] : null;
}

function srgbChannel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * srgbChannel(r) + 0.7152 * srgbChannel(g) + 0.0722 * srgbChannel(b);
}

function wcagContrast(l1: number, l2: number): number {
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

type ConciergeNavButtonProps = {
  cName: string | null;
  onClick?: () => void;
  className?: string;
};

export const ConciergeNavButton = ({ cName, onClick, className }: ConciergeNavButtonProps): JSX.Element | null => {
  const router = useRouter();
  const { href } = usePaths();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [metrics, setMetrics] = useState<{ w: number; h: number; radius: number } | null>(null);
  const [needsDarkening, setNeedsDarkening] = useState(false);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return undefined;
    const update = (): void =>
      setMetrics({
        w: el.offsetWidth,
        h: el.offsetHeight,
        radius: parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0,
      });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;

    // Resolve --dcm-color-primary to its computed color value via a probe element
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;background-color:var(--dcm-color-primary)';
    el.appendChild(probe);
    const btnColor = getComputedStyle(probe).backgroundColor;
    el.removeChild(probe);

    // Find nearest opaque ancestor background (the nav bar)
    let navBg = 'rgb(255,255,255)';
    let ancestor = el.parentElement;
    while (ancestor) {
      const bg = getComputedStyle(ancestor).backgroundColor;
      if (bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
        navBg = bg;
        break;
      }
      ancestor = ancestor.parentElement;
    }

    const btnRgb = parseRgb(btnColor);
    const navRgb = parseRgb(navBg);
    if (btnRgb && navRgb) {
      const ratio = wcagContrast(relativeLuminance(btnRgb), relativeLuminance(navRgb));
      setNeedsDarkening(ratio < 2.5);
    }
  }, []);

  if (!cName) return null;

  const handleClick =
    onClick ??
    (() => {
      void router?.push(`${href}/concierge`);
    });

  const rectProps = metrics
    ? {
        x: SVG_PAD,
        y: SVG_PAD,
        width: metrics.w,
        height: metrics.h,
        rx: Math.min(metrics.radius, metrics.h / 2),
        pathLength: 100,
      }
    : null;

  return (
    <NavButton
      ref={buttonRef}
      type='button'
      className={className}
      onClick={handleClick}
      data-testid='concierge-nav-button'
    >
      <HoverGlow aria-hidden />
      <Face $darken={needsDarkening}>
        <IconWrap aria-hidden>
          <BlackSparkleIcon width={16} height={16} fill='currentColor' />
        </IconWrap>
        AI Concierge
      </Face>
      {metrics && rectProps && (
        <StrokeSvg aria-hidden viewBox={`0 0 ${metrics.w + SVG_PAD * 2} ${metrics.h + SVG_PAD * 2}`}>
          <BaseRing {...rectProps} />
          <GlowGroup>
            {/* static module-level arrays — index keys are safe */}
            {glowLayers.map((layer, i) => (
              <TaperedStroke
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                {...rectProps}
                $alpha={layer.alpha}
                $color={HIGHLIGHT_GOLD}
                $length={layer.length}
                $travel={layer.travel}
                $width={layer.width}
              />
            ))}
          </GlowGroup>
          {/* static module-level arrays — index keys are safe */}
          {lensLayers.map((layer, i) => (
            <TaperedStroke
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...rectProps}
              $alpha={layer.alpha}
              $color={LENS_GOLD}
              $length={layer.length}
              $pulse
              $travel={layer.travel}
              $width={layer.width}
            />
          ))}
          <HoverRing {...rectProps} />
        </StrokeSvg>
      )}
      <HoverShadeBand aria-hidden>
        <HoverShadeArc />
      </HoverShadeBand>
    </NavButton>
  );
};

const NavButton = styled.button`
  align-self: center;
  background: none;
  border: none;
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  position: relative;

  &:focus-visible {
    outline: 2px solid #0075e0;
    outline-offset: 3px;
  }
`;

const HoverGlow = styled.span`
  background: radial-gradient(closest-side, var(--dcm-color-primary), transparent);
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  filter: blur(12px);
  inset: -10px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 0.85;
  }
`;

const Face = styled.span<{ $darken: boolean }>`
  align-items: center;
  background: ${({ $darken }) =>
    $darken
      ? 'linear-gradient(rgba(0, 0, 0, 0.30), rgba(0, 0, 0, 0.30)), var(--dcm-color-primary)'
      : 'var(--dcm-color-primary)'};
  border-radius: var(--dct--customized-radius-buttons, 9999px);
  color: var(--dcm-cart-btn-text-active);
  display: flex;
  font-size: var(--dcm-font-size-md);
  font-weight: var(--dcm-cart-btn-font-weight);
  gap: 7px;
  padding: 8px 16px;
  position: relative;
  white-space: nowrap;
`;

const IconWrap = styled.span`
  align-items: center;
  display: flex;
`;

const StrokeSvg = styled.svg`
  inset: -${SVG_PAD}px;
  overflow: visible;
  pointer-events: none;
  position: absolute;
`;

const BaseRing = styled.rect`
  fill: none;
  stroke: rgba(255, 255, 255, 0.25);
  stroke-width: 1;
`;

const TaperedStroke = styled.rect<{
  $alpha: number;
  $color: string;
  $length: number;
  $pulse?: boolean;
  $travel: Keyframes;
  $width: number;
}>`
  fill: none;
  stroke: ${({ $color }) => $color};
  stroke-dasharray: ${({ $length }) => `${$length} ${100 - $length}`};
  stroke-opacity: ${({ $alpha }) => $alpha};
  stroke-width: ${({ $width }) => $width};
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${({ $pulse, $travel }) =>
      $pulse
        ? css`
            ${$travel} 5s linear infinite,
            ${colorPulse} 6s ease-in-out infinite
          `
        : css`
            ${$travel} 5s linear infinite
          `};
  }

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    animation-play-state: paused;
    opacity: 0;
  }
`;

const GlowGroup = styled.g`
  filter: blur(4px);
  opacity: 0.4;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 0;
  }
`;

const HoverRing = styled.rect`
  animation: ${baseTravel} 5s linear infinite;
  fill: none;
  opacity: 0;
  stroke: ${HIGHLIGHT_GOLD};
  stroke-dasharray: ${LENS_LENGTH} ${100 - LENS_LENGTH};
  stroke-linecap: round;
  stroke-width: 1.5;
  transition: stroke-dasharray 0.5s ease, stroke-width 0.5s ease, opacity 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    animation-play-state: paused;
    opacity: 1;
    stroke-dasharray: 101 0;
    stroke-width: 1;
  }
`;

const spin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const HoverShadeBand = styled.span`
  border-radius: calc(var(--dct--customized-radius-buttons, 9999px) + 1px);
  inset: -1px;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  opacity: 0;
  overflow: hidden;
  padding: 2px;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${NavButton}:hover &,
  ${NavButton}:focus-visible & {
    opacity: 1;
    transition: opacity 0.4s ease 0.5s;
  }
`;

const HoverShadeArc = styled.span`
  background: conic-gradient(
    ${GREEN_TRANSPARENT} 250deg,
    ${LENS_GREEN} 296deg,
    ${LENS_GREEN} 312deg,
    ${GREEN_TRANSPARENT} 358deg
  );
  height: 260px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 260px;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 7s linear infinite;
  }
`;
